/**
 * Runtime configuration — the single typed accessor for app config.
 *
 * Production: the web container substitutes values into `window.__ENV__` at
 * start (docker-entrypoint → env.js), so config is runtime-, not build-, bound.
 * Dev: falls back to Vite `VITE_*` vars. Unsubstituted placeholders (`%VAR%`)
 * are treated as absent.
 */
declare global {
  interface Window {
    __ENV__?: Record<string, string>;
  }
}

function read(key: string): string {
  const runtime = typeof window !== "undefined" ? window.__ENV__?.[key] : undefined;
  if (runtime && !runtime.startsWith("%")) return runtime;
  const fromVite = (import.meta.env as Record<string, string | undefined>)[`VITE_${key}`];
  return fromVite ?? "";
}

export const env = {
  API_BASE_URL: read("API_BASE_URL") || "https://api.anyaistudio.com",
  CLERK_PUBLISHABLE_KEY: read("CLERK_PUBLISHABLE_KEY"),
  // Dev-only Clerk bypass for local testing. Gated on `import.meta.env.DEV`, so a
  // production build ignores it entirely. Only useful with the backend also run
  // as `DEV_AUTH_BYPASS=1` (otherwise data requests still 401). See RequireAuth.
  AUTH_BYPASS:
    import.meta.env.DEV && (read("AUTH_BYPASS") === "1" || read("AUTH_BYPASS") === "true"),
  // Analytics — powers the paywall A/B (empty key ⇒ analytics is a no-op).
  // Host defaults to PostHog EU (matches the iOS app); override via env if moved.
  POSTHOG_KEY: read("POSTHOG_KEY"),
  POSTHOG_HOST: read("POSTHOG_HOST") || "https://eu.i.posthog.com",
} as const;
